import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Key } from "@phosphor-icons/react";

const RULES = [
  ["require_upper", "Require an uppercase letter (A–Z)"],
  ["require_lower", "Require a lowercase letter (a–z)"],
  ["require_number", "Require a number (0–9)"],
  ["require_special", "Require a special character (!@#$…)"],
  ["block_common", "Block common / easily-guessed passwords"],
];

export default function PasswordPolicy() {
  const [pol, setPol] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/password-policy").then((r) => { setPol(r.data); setForm(r.data); });
  useEffect(() => { load(); }, []);

  const normalize = (f) => ({
    ...f,
    min_length: Number(f.min_length),
    password_age_days: Number(f.password_age_days ?? 0),
    prevent_reuse_last_n: Number(f.prevent_reuse_last_n ?? 0),
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/password-policy", normalize(form));
      setPol(data); setForm(data);
      toast.success("Password policy updated for this entity.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the policy.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;
  const dirty = pol && JSON.stringify(normalize(form)) !== JSON.stringify(normalize(pol));

  return (
    <div className="border border-border bg-card p-6 max-w-2xl" data-testid="password-policy">
      <div className="flex items-start gap-3">
        <Key size={22} className="mt-0.5 text-primary shrink-0" />
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg">Password policy</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Set the password rules for everyone in this entity. Applies wherever a password is created or changed.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <Label htmlFor="min-length">Minimum length</Label>
        <Input id="min-length" data-testid="min-length-input" type="number" min={8} max={128}
          value={form.min_length} onChange={(e) => setForm({ ...form, min_length: e.target.value })}
          className="rounded-none mt-1 w-28" />
        <p className="text-xs text-muted-foreground mt-1">8–128 characters. NIST recommends ≥ 12.</p>
      </div>

      <div className="mt-5 space-y-3">
        {RULES.map(([key, label]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-sm">{label}</span>
            <Switch data-testid={`toggle-${key}`} checked={!!form[key]}
              onCheckedChange={(v) => setForm({ ...form, [key]: v })} />
          </div>
        ))}
      </div>

      <div className="mt-6 border-t border-border pt-5 space-y-5">
        <div>
          <Label htmlFor="password-age">Password expiry (days)</Label>
          <Input id="password-age" data-testid="password-age-input" type="number" min={0} max={3650}
            value={form.password_age_days ?? 0}
            onChange={(e) => setForm({ ...form, password_age_days: e.target.value })}
            className="rounded-none mt-1 w-28" />
          <p className="text-xs text-muted-foreground mt-1">
            Force a password change after this many days. Use <span className="font-medium">0</span> to never expire.
          </p>
          {Number(form.password_age_days) > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 mt-2" data-testid="password-age-warning">
              Heads up: accounts whose passwords are already older than this will be prompted to reset on their next sign-in.
              They can recover instantly via the forced-reset screen — no one is locked out.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="reuse-count">Prevent reuse of last</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input id="reuse-count" data-testid="prevent-reuse-input" type="number" min={0} max={24}
              value={form.prevent_reuse_last_n ?? 0}
              onChange={(e) => setForm({ ...form, prevent_reuse_last_n: e.target.value })}
              className="rounded-none w-28" />
            <span className="text-sm text-muted-foreground">passwords</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Block reusing a recent password (0–24). Use <span className="font-medium">0</span> to allow reuse.
          </p>
        </div>
      </div>

      <Button data-testid="save-password-policy" onClick={save} disabled={saving || !dirty}
        className="rounded-none bg-primary hover:bg-primary/90 mt-6">{saving ? "Saving…" : "Save policy"}</Button>
    </div>
  );
}
